import { useState, useEffect, useMemo, useCallback, createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import Fuse from "fuse.js";
import { searchableItems } from "@/utils/searchData";

// Create a context for global search state
const GlobalSearchContext = createContext(null);

/**
 * Global Search Provider - Wrap your app with this
 */
export const GlobalSearchProvider = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [query, setQuery] = useState("");
    const [selectedIndex, setSelectedIndex] = useState(0);
    const navigate = useNavigate();

    // Initialize Fuse.js with search configuration
    const fuse = useMemo(
        () =>
            new Fuse(searchableItems, {
                keys: [
                    { name: "title", weight: 3 },
                    { name: "keywords", weight: 2 },
                    { name: "description", weight: 1 },
                    { name: "parent", weight: 1 },
                ],
                threshold: 0.4, // Moderate fuzziness (0 = exact, 1 = match anything)
                ignoreLocation: true,
                minMatchCharLength: 2,
                includeScore: true,
            }),
        []
    );

    // Perform fuzzy search
    const searchResults = useMemo(() => {
        if (!query.trim()) {
            // Show all items when no query, but limit to 10
            return searchableItems.slice(0, 10);
        }

        const fuseResults = fuse.search(query);
        // Return top 10 most relevant results
        return fuseResults.slice(0, 10).map((result) => result.item);
    }, [query, fuse]);

    // Keyboard shortcut handler (Ctrl+K / Cmd+K)
    useEffect(() => {
        const handleKeyDown = (e) => {
            // Check for Ctrl+K (Windows/Linux) or Cmd+K (Mac)
            if ((e.ctrlKey || e.metaKey) && e.key === "k") {
                e.preventDefault();
                setIsOpen((prev) => !prev);
            }

            // ESC to close
            if (e.key === "Escape" && isOpen) {
                close();
            }

            // Arrow navigation when modal is open
            if (isOpen && searchResults.length > 0) {
                if (e.key === "ArrowDown") {
                    e.preventDefault();
                    setSelectedIndex((prev) =>
                        prev < searchResults.length - 1 ? prev + 1 : 0
                    );
                } else if (e.key === "ArrowUp") {
                    e.preventDefault();
                    setSelectedIndex((prev) =>
                        prev > 0 ? prev - 1 : searchResults.length - 1
                    );
                } else if (e.key === "Enter") {
                    e.preventDefault();
                    if (searchResults[selectedIndex]) {
                        handleNavigate(searchResults[selectedIndex]);
                    }
                }
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [isOpen, searchResults, selectedIndex]);

    // Reset selected index when query changes
    useEffect(() => {
        setSelectedIndex(0);
    }, [query]);

    // Reset query when modal closes
    useEffect(() => {
        if (!isOpen) {
            setQuery("");
            setSelectedIndex(0);
        }
    }, [isOpen]);

    // Modal control functions
    const open = useCallback(() => setIsOpen(true), []);
    const close = useCallback(() => setIsOpen(false), []);
    const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

    // Navigation handler
    const handleNavigate = useCallback(
        (item) => {
            if (!item) return;

            // If it's a tab type, store the tab name in sessionStorage
            // BEFORE navigation so the page can pick it up
            if (item.type === "tab" && item.tabName) {
                sessionStorage.setItem("autoSelectTab", item.tabName);
                // Add timestamp to force effect re-run
                sessionStorage.setItem("autoSelectTabTimestamp", Date.now().toString());
            }

            // Navigate to the route
            navigate(item.route);

            // Close modal after navigation
            setIsOpen(false);
        },
        [navigate]
    );

    const value = {
        isOpen,
        open,
        close,
        toggle,
        query,
        setQuery,
        searchResults,
        selectedIndex,
        setSelectedIndex,
        handleNavigate,
    };

    return <GlobalSearchContext.Provider value={value}>{children}</GlobalSearchContext.Provider>;
};

/**
 * Hook to use global search context
 */
export const useGlobalSearch = () => {
    const context = useContext(GlobalSearchContext);
    if (!context) {
        throw new Error("useGlobalSearch must be used within GlobalSearchProvider");
    }
    return context;
};

/**
 * Hook to auto-select tab from sessionStorage
 * Use this in pages with TabNavigation
 */
export const useAutoSelectTab = (tabs, setActiveTab) => {
    useEffect(() => {
        const autoSelectTab = sessionStorage.getItem("autoSelectTab");
        const timestamp = sessionStorage.getItem("autoSelectTabTimestamp");

        if (autoSelectTab && tabs.includes(autoSelectTab)) {
            setActiveTab(autoSelectTab);
            sessionStorage.removeItem("autoSelectTab");
            sessionStorage.removeItem("autoSelectTabTimestamp");
        }
    }, [tabs, setActiveTab]);

    // Listen for sessionStorage changes from navigation
    useEffect(() => {
        const handleStorageChange = () => {
            const autoSelectTab = sessionStorage.getItem("autoSelectTab");
            if (autoSelectTab && tabs.includes(autoSelectTab)) {
                setActiveTab(autoSelectTab);
                sessionStorage.removeItem("autoSelectTab");
                sessionStorage.removeItem("autoSelectTabTimestamp");
            }
        };

        // Poll for changes (since storage events don't work for same-tab changes)
        const interval = setInterval(handleStorageChange, 100);

        return () => clearInterval(interval);
    }, [tabs, setActiveTab]);
};
